<template>
    <div class="ds-ex-panel">
        <div class="ds-ex-panel-head">
            <h2 class="ds-ex-panel-title">{{ vacancy.position.name }} · {{ dateLabel }}</h2>
            <div class="ds-ex-chips-cell">
                <span v-if="chip" class="ds-ex-chip" :class="`is-${chip.tone}`">{{ chip.label }}</span>
                <span v-if="urgent" class="ds-ex-chip is-hot">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2c1 3.5 5 6 5 11a5 5 0 0 1-10 0c0-2.5 1.2-4 2.5-5.2.2 1.7 1 2.7 2 3.2C11 8 11.5 5 12 2z" /></svg>
                    Горящая
                </span>
                <span class="ds-ex-panel-responses" :class="{ 'is-brand': vacancy.responses.length }">{{ responsesText }}</span>
            </div>
            <div class="ds-glass-segment ds-ex-panel-tabs" role="tablist" aria-label="Разделы вакансии">
                <button type="button" role="tab" class="ds-glass-segment-option" :class="{ 'is-active': tab === 'vacancy' }" :aria-selected="tab === 'vacancy'" @click="tab = 'vacancy'">Вакансия</button>
                <button type="button" role="tab" class="ds-glass-segment-option" :class="{ 'is-active': tab === 'history' }" :aria-selected="tab === 'history'" @click="tab = 'history'">
                    История <span v-if="historyStatus === 'ready'" class="ds-ex-muted">{{ history.length }}</span>
                </button>
            </div>
        </div>

        <div class="ds-ex-panel-body">
            <div v-if="note" class="ds-ex-note" :class="`is-${note.tone}`">
                <svg v-if="note.tone === 'open'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true"><path d="M4 20h4L19 9l-4-4L4 16v4z" /></svg>
                <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
                <span>{{ note.text }}</span>
            </div>

            <template v-if="tab === 'vacancy'">
                <label class="ds-ex-panel-field">
                    <span class="ds-ex-label">Магазин</span>
                    <span class="ds-ex-field is-readonly">{{ vacancy.department.name }}</span>
                </label>
                <div v-if="vacancy.isRequest" class="ds-ex-panel-field">
                    <span class="ds-ex-label">Запрошен сотрудник</span>
                    <span class="ds-ex-field is-readonly">{{ requestedText }}</span>
                </div>
                <div class="ds-ex-panel-row">
                    <div class="ds-ex-panel-field ds-ex-grow">
                        <span class="ds-ex-label">Дата</span>
                        <div class="ds-ex-date" :class="{ 'is-disabled': !editable }"><AppDateInput :model-value="dayjs(form.date)" :min-date="today" @update:model-value="onDate" /></div>
                    </div>
                    <div class="ds-ex-panel-field">
                        <span class="ds-ex-label">Начало</span>
                        <div class="ds-ex-time is-wide"><TimePicker v-model="form.start" :disabled="!editable" /></div>
                    </div>
                    <div class="ds-ex-panel-field">
                        <span class="ds-ex-label">Окончание</span>
                        <div class="ds-ex-time is-wide"><TimePicker v-model="form.end" :disabled="!editable" /></div>
                    </div>
                </div>
                <div v-if="timeChanged" class="ds-ex-muted ds-ex-panel-was">Было {{ initial.start }} – {{ initial.end }}</div>
                <div class="ds-ex-panel-field">
                    <span class="ds-ex-label">Должность</span>
                    <ExchangeSelect v-model="form.positionId" label="Должность" searchable search-placeholder="Найти должность" :options="positionOptions" :disabled="!editable" />
                </div>
                <div v-if="showExchangeType" class="ds-ex-panel-field">
                    <span class="ds-ex-label">Биржа</span>
                    <div class="ds-glass-segment ds-ex-segment is-stretch" role="group" aria-label="Тип биржи">
                        <button
                            v-for="type in EXCHANGE_TYPES"
                            :key="type"
                            type="button"
                            class="ds-glass-segment-option"
                            :class="{ 'is-active': form.exchangeType === type }"
                            :aria-pressed="form.exchangeType === type"
                            :disabled="!editable"
                            @click="form.exchangeType = type"
                        >{{ EXCHANGE_TYPE_LABELS[type] }}</button>
                    </div>
                </div>
                <label class="ds-ex-panel-field">
                    <span class="ds-ex-label">Комментарий</span>
                    <textarea v-model="form.comment" class="ds-ex-field ds-ex-textarea" maxlength="100" rows="3" :disabled="!editable"></textarea>
                </label>
                <div v-if="formError" class="ds-ex-error" role="alert">{{ formError }}</div>
                <div class="ds-ex-muted">«Горящая» ставится автоматически.</div>
            </template>

            <VacancyHistoryFeed v-else :records="history" :status="historyStatus" @retry="loadHistory" />
        </div>

        <div class="ds-ex-panel-foot">
            <button v-if="canRemove" type="button" class="ds-ex-danger-link" :disabled="saving || busy" @click="emit('remove')">{{ vacancy.isRequest ? 'Удалить запрос' : 'Снять вакансию' }}</button>
            <div class="ds-ex-spacer"></div>
            <template v-if="tab === 'vacancy' && editable">
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" :disabled="saving" @click="emit('close')">Отмена</button>
                <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="!canSave" @click="save">{{ saving ? 'Сохраняем…' : 'Сохранить' }}</button>
            </template>
            <template v-else>
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="emit('close')">Закрыть</button>
                <button v-if="vacancy.responses.length && !readonly" type="button" class="ds-glass-btn ds-glass-btn-primary" @click="emit('to-responses')">К откликам</button>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import AppDateInput from '@/ui/AppDateInput.vue'
import TimePicker from '@/components/TimePicker.vue'
import { useCalendarToday } from '@/ui/calendarTime'
import { useAuthStore } from '@/stores/authStore'
import ExchangeSelect from './ExchangeSelect.vue'
import VacancyHistoryFeed from './VacancyHistoryFeed.vue'
import { exchangeApi } from './exchangeApi'
import { errorText, useExchangeGuard } from './exchangeActions'
import { exchangeToast } from './exchangeToasts'
import { useExchangeDirectory } from './exchangeDirectory'
import {
    buildTimestamp,
    countLabel,
    dayKeyOf,
    EXCHANGE_TYPE_LABELS,
    exchangeFlags,
    fullName,
    instantOf,
    isUrgent,
    archiveChip,
    requestChip,
    vacancyChip,
    RESPONSE_FORMS,
    rowDateLabel,
    wallNow,
    workTime,
} from './exchangeFormat'
import type { ExchangeError, ExchangeType, HistoryRecord, Vacancy } from './exchangeTypes'

const props = defineProps<{
    vacancy: Vacancy
    initialTab: 'vacancy' | 'history'
    // Архив и чужие записи — только чтение.
    readonly: boolean
    canManage: boolean
}>()

const emit = defineEmits<{ close: [], changed: [], remove: [], 'to-responses': [] }>()

const EXCHANGE_TYPES: ExchangeType[] = ['internal', 'external', 'both']

const authStore = useAuthStore()
const directory = useExchangeDirectory()
const today = useCalendarToday()

const tab = ref<'vacancy' | 'history'>(props.initialTab)
const history = ref<HistoryRecord[]>([])
const historyStatus = ref<'loading' | 'ready' | 'error'>('loading')
const saving = ref(false)
const formError = ref('')
// Сервер ответил vacancy_edit_locked — форма переходит в чтение до обновления данных.
const lockedByServer = ref(false)

const initial = {
    date: dayKeyOf(props.vacancy.start),
    start: workTime(props.vacancy.start).format('HH:mm'),
    end: workTime(props.vacancy.end).format('HH:mm'),
}
const form = reactive({
    date: initial.date,
    start: initial.start as string | null,
    end: initial.end as string | null,
    positionId: props.vacancy.position.id,
    exchangeType: props.vacancy.exchangeType,
    comment: props.vacancy.comment,
})

const showExchangeType = computed(() => Boolean(authStore.settings?.USE_EXCHANGE_PORTAL))
const positionOptions = computed(() => {
    const list = directory.positions.value.map(item => ({ id: item.id, name: item.name }))
    // Должность записи остаётся видимой, даже если её нет в текущем справочнике.
    if (!list.some(item => item.id === props.vacancy.position.id)) list.unshift({ id: props.vacancy.position.id, name: props.vacancy.position.name })
    return list
})

const dateLabel = computed(() => {
    const label = rowDateLabel(dayKeyOf(props.vacancy.start))
    return label.charAt(0).toLowerCase() + label.slice(1)
})
// Архивный итог — только в архиве; активная запись показывает текущее состояние, как строка списка.
const chip = computed(() => props.readonly ? archiveChip(props.vacancy) : props.vacancy.isRequest ? requestChip(props.vacancy) : vacancyChip(props.vacancy))
const { isBusy, guarded } = useExchangeGuard()
const busy = computed(() => isBusy(props.vacancy.id))
const urgent = computed(() => !props.readonly && isUrgent(props.vacancy))
const responsesText = computed(() => props.vacancy.responses.length ? countLabel(props.vacancy.responses.length, RESPONSE_FORMS) : props.vacancy.assigned ? fullName(props.vacancy.assigned) : 'Нет откликов')
const requestedText = computed(() => props.vacancy.requestedUser ? fullName(props.vacancy.requestedUser) : '—')

// Возможность правки — только can_edit backend (учитывает постоянный запрет после первого отклика).
const editable = computed(() => !props.readonly && props.canManage && props.vacancy.canEdit && !lockedByServer.value && !props.vacancy.isRequest)
const canRemove = computed(() => !props.readonly && props.canManage && props.vacancy.canDelete)

const note = computed<{ tone: 'open' | 'muted', text: string } | null>(() => {
    if (props.readonly) return null
    if (editable.value) return { tone: 'open', text: 'Откликов нет — вакансию можно изменить. После первого отклика редактирование закроется.' }
    if (props.vacancy.isRequest) return { tone: 'muted', text: 'Условия запроса не меняются. Его можно удалить и создать новый.' }
    if (props.vacancy.assigned) return { tone: 'muted', text: 'Сотрудник назначен — изменить вакансию нельзя.' }
    if (props.vacancy.responses.length) return { tone: 'muted', text: 'Есть отклики — изменить вакансию уже нельзя. Можно назначить сотрудника или снять вакансию.' }
    if (props.vacancy.hasEverReceivedResponse || lockedByServer.value) return { tone: 'muted', text: 'По вакансии уже были отклики — изменить её нельзя. Можно снять вакансию и создать новую.' }
    return { tone: 'muted', text: 'Изменение вакансии недоступно.' }
})

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/
const timeChanged = computed(() => form.start !== initial.start || form.end !== initial.end)
const dirty = computed(() => timeChanged.value || form.date !== initial.date || form.positionId !== props.vacancy.position.id
    || form.exchangeType !== props.vacancy.exchangeType || form.comment.trim() !== props.vacancy.comment.trim())
const valid = computed(() => Boolean(form.date && form.start && form.end && TIME.test(form.start) && TIME.test(form.end) && form.start < form.end && form.positionId))
const canSave = computed(() => editable.value && dirty.value && valid.value && !saving.value && !busy.value)

watch(() => [form.date, form.start, form.end], () => { formError.value = '' })

function onDate(value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) {
    if (dayjs.isDayjs(value)) form.date = value.format('YYYY-MM-DD')
}

async function loadHistory() {
    historyStatus.value = 'loading'
    try {
        history.value = await exchangeApi.history(props.vacancy.id)
        historyStatus.value = 'ready'
    } catch {
        historyStatus.value = 'error'
    }
}

async function save() {
    if (!canSave.value) return
    if (instantOf(form.date, form.start!) < wallNow()) {
        formError.value = 'Дата и время начала вакансии не могут быть раньше текущего времени'
        return
    }
    saving.value = true
    // PUT существующей формы: только записываемые поля, ставка — прежнее значение без поля в UI.
    const payload: Record<string, unknown> = {
        date: form.date,
        dttm_work_start_at: buildTimestamp(form.date, form.start!),
        dttm_work_end_at: buildTimestamp(form.date, form.end!),
        department: props.vacancy.department.id,
        position: form.positionId,
        ...exchangeFlags(showExchangeType.value ? form.exchangeType : props.vacancy.exchangeType),
        comment: form.comment.trim(),
        salary_hour: props.vacancy.raw.salary_hour ?? null,
    }
    try {
        // PUT — под общей блокировкой вакансии, как назначение и снятие.
        const done = await guarded(props.vacancy.id, async () => {
            await exchangeApi.update(props.vacancy.id, payload)
            return true
        })
        if (!done) return
        exchangeToast.success('Вакансия сохранена', { text: `${props.vacancy.position.name} · ${form.start}–${form.end}`, key: `update:${props.vacancy.id}` })
        emit('changed')
        emit('close')
    } catch (error) {
        const e = error as ExchangeError
        if (e?.code === 'vacancy_edit_locked') {
            lockedByServer.value = true
            emit('changed')
        }
        exchangeToast.error('Не удалось сохранить', { text: errorText(error, 'Попробуйте ещё раз'), key: `update:${props.vacancy.id}` })
    } finally {
        saving.value = false
    }
}

onMounted(loadHistory)
</script>
